import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field, Pill, cx } from "@/components/kit";

const STEPS = ["Compte", "Objectif", "Budget", "Marge minimum", "Zones", "Catégories", "Marketplace", "Permissions", "Synchronisation", "Morning Brief"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(Math.min(user?.workspace?.onboarding_step || 1, 10));
  const [d, setD] = useState({ first_name: user?.first_name || "", seller_type: "particulier", monthly_goal: "", budget: "", min_margin: "", zones: "", categories: "" });
  const [account, setAccount] = useState(null);
  const [perm, setPerm] = useState("ASSISTANT");
  const [brief, setBrief] = useState(null);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });

  const next = async () => {
    try {
      const data = {};
      if (step === 1) Object.assign(data, { first_name: d.first_name, seller_type: d.seller_type });
      if (step === 2) data.monthly_goal = Number(d.monthly_goal) || null;
      if (step === 3) data.budget = Number(d.budget) || null;
      if (step === 4) data.min_margin = Number(d.min_margin) || null;
      if (step === 5) data.zones = d.zones.split(",").map((s) => s.trim()).filter(Boolean);
      if (step === 6) data.categories = d.categories.split(",").map((s) => s.trim()).filter(Boolean);
      if (step === 8) await api.put("/autopilot", { level: perm });
      if (step === 10) { await api.put("/me/onboarding", { step: 10, data }); await refresh(); return nav("/"); }
      await api.put("/me/onboarding", { step, data });
      if (step === 9) setBrief((await api.get("/morning-brief")).data);
      setStep(step + 1);
    } catch (e) { fail(e); }
  };
  const connect = async () => {
    try { setAccount((await api.post("/connectors/accounts", { marketplace: "leboncoin" })).data); } catch (e) { fail(e); }
  };

  return (
    <div className="min-h-screen bg-black grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-2xl card p-8 glow" data-testid="onboarding">
        <div className="flex gap-1 mb-8">{STEPS.map((s, i) => <div key={s} className={cx("h-1 flex-1 rounded-full", i < step ? "bg-[#7CFF00]" : "bg-white/10")} />)}</div>
        <div className="mono text-[11px] tracking-[.25em] text-[#7CFF00]">ÉTAPE {step}/10</div>
        <h1 className="text-3xl font-black mt-2 mb-6" data-testid="onboarding-step-title">{STEPS[step - 1]}</h1>
        <div className="space-y-4">
          {step === 1 && <>
            <Field label="Prénom"><input className="inp" value={d.first_name} onChange={set("first_name")} data-testid="onb-first-name" /></Field>
            <Field label="Statut vendeur"><select className="inp" value={d.seller_type} onChange={set("seller_type")} data-testid="onb-seller-type"><option value="particulier">Particulier</option><option value="pro">Professionnel</option></select></Field>
          </>}
          {step === 2 && <Field label="Objectif de bénéfice mensuel (€)"><input type="number" className="inp" value={d.monthly_goal} onChange={set("monthly_goal")} data-testid="onb-goal" placeholder="2000" /></Field>}
          {step === 3 && <Field label="Budget d'achat mensuel (€)"><input type="number" className="inp" value={d.budget} onChange={set("budget")} data-testid="onb-budget" placeholder="1000" /></Field>}
          {step === 4 && <Field label="Marge minimum par produit (€)"><input type="number" className="inp" value={d.min_margin} onChange={set("min_margin")} data-testid="onb-min-margin" placeholder="30" /></Field>}
          {step === 5 && <Field label="Zones (séparées par des virgules)"><input className="inp" value={d.zones} onChange={set("zones")} data-testid="onb-zones" placeholder="Lyon, Villeurbanne" /></Field>}
          {step === 6 && <Field label="Catégories (séparées par des virgules)"><input className="inp" value={d.categories} onChange={set("categories")} data-testid="onb-categories" placeholder="Consoles, Mode" /></Field>}
          {step === 7 && <div className="space-y-3 text-sm text-[#A0AAB2]">
            <p>LBC Agent fonctionne sans marketplace. Connecte Leboncoin pour synchroniser via l'extension navigateur (lecture de tes pages, actions assistées).</p>
            {account ? <Pill value="AVAILABLE" label="Compte Leboncoin ajouté" testid="onb-account-added" /> : <Btn variant="ghost" onClick={connect} data-testid="onb-connect-lbc">Connecter Leboncoin</Btn>}
          </div>}
          {step === 8 && <div className="grid sm:grid-cols-3 gap-3">
            {[["COPILOT", "Analyse et recommande, aucune action."], ["ASSISTANT", "Prépare, tu valides."], ["AUTOPILOT", "Exécute ce que tu autorises."]].map(([k, t]) => (
              <button key={k} onClick={() => setPerm(k)} data-testid={`onb-level-${k}`} className={cx("card p-4 text-left", perm === k && "glow")}>
                <div className="font-display font-bold">{k}</div><div className="text-xs text-[#A0AAB2] mt-1">{t}</div></button>))}
            <p className="sm:col-span-3 text-xs text-[#626E77]">Publication, suppression et acceptation d'offre restent en validation (ASK) par défaut.</p>
          </div>}
          {step === 9 && <p className="text-sm text-[#A0AAB2]">Première synchronisation : installe l'extension (page Connecteurs) ou importe un CSV (Paramètres). Tu peux aussi continuer et le faire plus tard.</p>}
          {step === 10 && brief && <div className="text-sm space-y-2" data-testid="onb-brief">
            <div className="text-xl font-display font-bold">{brief.greeting} 👋</div>
            <div className="text-[#A0AAB2]">Ton premier Morning Brief est prêt. Action n°1 : {brief.action_1?.title || "importer tes premières données"}.</div>
          </div>}
        </div>
        <div className="flex justify-between mt-8">
          <Btn variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="onb-back">Retour</Btn>
          <Btn onClick={next} data-testid="onb-next">{step === 10 ? "Entrer dans LBC Agent" : "Continuer"}</Btn>
        </div>
      </div>
    </div>
  );
}
